import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Mountain, Calendar, MapPin, Clock, Users, 
  ExternalLink, ChevronRight, Filter, Search, Loader2, Music, Ticket
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPE_ICONS = {
  music_festival: Music,
  cultural_festival: Users,
  motorcycle_rally: Ticket,
  motorsport_rally: Ticket,
  literature_festival: Ticket
};

const EventsListing = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    event_type: '',
    state: '',
    month: ''
  });
  const [sources, setSources] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async (appliedFilters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedFilters.event_type) params.set('event_type', appliedFilters.event_type);
      if (appliedFilters.state) params.set('state', appliedFilters.state);
      if (appliedFilters.month) params.set('month', appliedFilters.month);

      const response = await fetch(`${API_URL}/api/content/events?${params.toString()}`);
      const data = await response.json();
      setEvents(data.events || []);
      setSources(data.sources || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    fetchEvents(filters);
  };

  const getEventTypeLabel = (type) => {
    const labels = {
      music_festival: 'Music Festival',
      cultural_festival: 'Cultural Festival',
      motorcycle_rally: 'Motorcycle Rally',
      motorsport_rally: 'Motorsport Rally',
      literature_festival: 'Literature Festival'
    };
    return labels[type] || type?.replace('_', ' ');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Mountain className="h-8 w-8 text-[#2C5F8D]" />
              <span className="text-xl font-bold text-[#2C5F8D]" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/stays" className="text-gray-600 hover:text-gray-900">Stays</Link>
              <Link to="/treks" className="text-gray-600 hover:text-gray-900">Treks</Link>
              <Link to="/events" className="text-[#DEB1FF] font-medium">Events</Link>
            </nav>

            <div className="flex items-center gap-3">
              <Link to="/partner">
                <Button variant="outline" size="sm">Become a Host</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#DEB1FF] to-[#C98FFF] text-gray-800 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
            Himalayan Festivals & Events
          </h1>
          <p className="text-gray-600 max-w-2xl">
            Experience the vibrant culture of the Himalayas through music festivals, cultural celebrations, and adventure rallies.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={filters.event_type}
              onChange={(e) => setFilters({...filters, event_type: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm"
              data-testid="filter-type"
            >
              <option value="">All Types</option>
              <option value="music_festival">Music Festival</option>
              <option value="cultural_festival">Cultural Festival</option>
              <option value="motorcycle_rally">Motorcycle Rally</option>
              <option value="literature_festival">Literature Festival</option>
            </select>

            <select
              value={filters.state}
              onChange={(e) => setFilters({...filters, state: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm"
              data-testid="filter-state"
            >
              <option value="">All Regions</option>
              <option value="Arunachal">Arunachal Pradesh</option>
              <option value="Nagaland">Nagaland</option>
              <option value="Himachal">Himachal Pradesh</option>
              <option value="Ladakh">Ladakh</option>
              <option value="Sikkim">Sikkim</option>
            </select>

            <select
              value={filters.month}
              onChange={(e) => setFilters({...filters, month: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm"
              data-testid="filter-month"
            >
              <option value="">All Months</option>
              <option value="February">February</option>
              <option value="July">July</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="December">December</option>
            </select>

            <Button onClick={handleSearch} className="bg-[#DEB1FF] hover:bg-[#C98FFF] text-gray-800">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Disclaimer */}
        <div className="bg-[#DEB1FF]/20 border border-[#DEB1FF] rounded-xl p-4 mb-6">
          <p className="text-sm text-gray-700">
            <strong>Note:</strong> Event dates vary each year. Please visit the official websites for confirmed dates and registration.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#DEB1FF]" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No events found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const EventIcon = EVENT_TYPE_ICONS[event.event_type] || Calendar;
              
              return (
                <div 
                  key={event.content_id} 
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                  data-testid={`event-card-${event.content_id}`}
                >
                  {/* Image */}
                  <div className="relative h-48 bg-gray-100 overflow-hidden">
                    {event.images && event.images[0] ? (
                      <img 
                        src={event.images[0]} 
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#DEB1FF]/30">
                        <EventIcon className="w-12 h-12 text-[#DEB1FF]" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-[#DEB1FF] text-gray-800 text-xs font-medium rounded-full">
                        {getEventTypeLabel(event.event_type)}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">{event.title}</h3>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <MapPin className="w-4 h-4" />
                      <span>{event.location?.city}, {event.location?.state}</span>
                    </div>

                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{event.description}</p>

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {event.typical_dates}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {event.duration_days} days
                      </span>
                    </div>

                    {/* Highlights */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {event.highlights?.slice(0, 3).map((highlight, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {highlight}
                        </span>
                      ))}
                    </div>

                    {/* Price */}
                    {event.price_range?.max > 0 && (
                      <div className="mb-3">
                        <span className="text-lg font-bold text-[#B388EB]">
                          ₹{event.price_range?.min?.toLocaleString()}
                        </span>
                        {event.price_range?.max > event.price_range?.min && (
                          <span className="text-sm text-gray-500"> - ₹{event.price_range?.max?.toLocaleString()}</span>
                        )}
                      </div>
                    )}

                    {/* Source Link */}
                    <a 
                      href={event.source?.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-2 border border-[#DEB1FF] text-gray-700 rounded-lg hover:bg-[#DEB1FF]/20 transition-colors text-sm"
                    >
                      Visit {event.source?.name}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Sources */}
        <div className="mt-12 pt-8 border-t">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Sources</h3>
          <div className="flex flex-wrap gap-4">
            {sources.map((source, idx) => (
              <a 
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 text-sm"
              >
                <ExternalLink className="w-4 h-4 text-gray-400" />
                {source.name}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsListing;
